import React, { useState, useEffect } from 'react';
import { requestOTP, verifyOTP } from '../services/api';

export default function Login({ onLogin, onRegister }) {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleRequestOTP = async () => {
    if (phone.length !== 10) {
      setError('Enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await requestOTP(phone);
      setStep('otp');
      setCountdown(600);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');
    setOtp('');
    try {
      await requestOTP(phone);
      setCountdown(600);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setError('Enter the 6-digit OTP');
      return;
    }
    if (countdown === 0) {
      setError('OTP has expired. Please request a new one.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await verifyOTP(phone, otp);
      onLogin(
        {
          name: res.data.landlord_name,
          id: res.data.landlord_id,
          referral_code: res.data.referral_code || ''
        },
        res.data.access_token
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a56db 0%, #1e40af 100%)'
    }}>
      <div style={{
        background: 'white', borderRadius: 16,
        padding: 40, width: 360,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{
            fontSize: 28, fontWeight: 700,
            color: '#1a56db', marginBottom: 4
          }}>RentOS</h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            The Rental Operating System
          </p>
        </div>

        {step === 'phone' ? (
          <>
            <p style={{
              color: '#374151', marginBottom: 8,
              fontWeight: 500, fontSize: 14
            }}>
              Mobile Number
            </p>
            <input
              type="tel"
              placeholder="Enter 10-digit number"
              value={phone}
              onChange={e => setPhone(
                e.target.value.replace(/\D/g, '').slice(0, 10)
              )}
              onKeyDown={e => e.key === 'Enter' && handleRequestOTP()}
              style={{
                width: '100%', padding: '12px 14px',
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 15, marginBottom: 16,
                boxSizing: 'border-box'
              }}
            />
            <button
              onClick={handleRequestOTP}
              disabled={loading}
              style={{
                width: '100%', padding: 13,
                background: loading ? '#93c5fd' : '#1a56db',
                color: 'white', borderRadius: 8,
                fontSize: 15, fontWeight: 600,
                border: 'none', cursor: 'pointer',
                marginBottom: 12
              }}
            >
              {loading ? 'Sending OTP...' : 'Get OTP on WhatsApp'}
            </button>
            <div style={{
              textAlign: 'center', borderTop: '1px solid #f1f5f9',
              paddingTop: 16, marginTop: 4
            }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>
                New to RentOS?{' '}
              </span>
              <button
                onClick={onRegister}
                style={{
                  background: 'none', border: 'none',
                  color: '#1a56db', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Register here
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{
              color: '#374151', marginBottom: 4,
              fontWeight: 500, fontSize: 14
            }}>
              OTP sent to +91 {phone}
            </p>
            <p style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
              Check your WhatsApp for the 6-digit code
            </p>
            <input
              type="number"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={e => setOtp(e.target.value.slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()}
              style={{
                width: '100%', padding: '12px 14px',
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 20, letterSpacing: 6,
                textAlign: 'center', marginBottom: 8,
                boxSizing: 'border-box'
              }}
            />

            {countdown > 0 ? (
              <p style={{
                color: countdown < 60 ? '#dc2626' : '#64748b',
                fontSize: 12, textAlign: 'center', marginBottom: 12
              }}>
                OTP expires in {formatCountdown(countdown)}
              </p>
            ) : (
              <p style={{
                color: '#dc2626', fontSize: 12,
                textAlign: 'center', marginBottom: 12
              }}>
                OTP expired
              </p>
            )}

            <button
              onClick={handleVerifyOTP}
              disabled={loading || countdown === 0}
              style={{
                width: '100%', padding: 13,
                background: (loading || countdown === 0) ? '#93c5fd' : '#1a56db',
                color: 'white', borderRadius: 8,
                fontSize: 15, fontWeight: 600,
                border: 'none', cursor: (loading || countdown === 0) ? 'not-allowed' : 'pointer',
                marginBottom: 8
              }}
            >
              {loading ? 'Verifying...' : 'Login'}
            </button>

            <button
              onClick={handleResendOTP}
              disabled={resendLoading || countdown > 540}
              style={{
                width: '100%', padding: 10,
                background: 'transparent',
                border: '1px solid #e2e8f0',
                color: (resendLoading || countdown > 540) ? '#94a3b8' : '#1a56db',
                borderRadius: 8, fontSize: 13,
                cursor: (resendLoading || countdown > 540) ? 'not-allowed' : 'pointer',
                marginBottom: 8
              }}
            >
              {resendLoading ? 'Resending...' :
                countdown > 540 ? `Resend OTP in ${formatCountdown(countdown - 540)}` :
                'Resend OTP'}
            </button>

            <button
              onClick={() => {
                setStep('phone');
                setError('');
                setOtp('');
                setCountdown(0);
              }}
              style={{
                width: '100%', padding: 10,
                background: 'transparent', border: 'none',
                color: '#64748b', fontSize: 13, cursor: 'pointer'
              }}
            >
              Change number
            </button>
          </>
        )}

        {error && (
          <p style={{
            color: '#dc2626', fontSize: 13,
            marginTop: 12, textAlign: 'center'
          }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}